































import { Vue, Component, Prop } from "vue-property-decorator";
import { mapState } from "vuex";

// Types
import { ICountry, IRegion } from "@/models/constants";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      regions(state: IState, getters) {
        return getters[`constants/getRegions`](this.countryCode);
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      countries: (state: IState, getters) => state.constants.countries
    })
  }
})
export default class AddressSearch extends Vue {
  public $refs!: {
    map: HTMLFormElement;
    addressSeach: HTMLFormElement;
  };

  // :getters
  public countries!: ICountry[];
  public regions!: IRegion[];

  @Prop({ default: false })
  public autofocus!: boolean;

  @Prop({ default: false })
  public isProcessing!: boolean;

  @Prop({ default: "" })
  public searchedValue!: string;

  public autocompleteService: any;
  public placesService: any;

  public addressCheckDebounce = this.$_.debounce(this.findPredictions, 500);

  public address = "";
  public isSearching = false;
  public addressSelected = false;
  public predictions: any[] = [];
  public componentWindow: any;

  public countryCode = "";

  public sessionToken = this.$uuid();

  public async created() {
    await this.$store.dispatch("constants/getCountries");

    if (this.searchedValue) {
      this.$set(this, "address", this.searchedValue);
    }
  }

  public get getRegions() {
    return this.regions;
  }

  public async mounted() {
    this.componentWindow = window;
    try {
      if (!(window as any)?.google?.maps?.places) await this.loadGoogleMapsJs();
      this.autocompleteService = new this.componentWindow.google.maps.places.AutocompleteService();
      this.sessionToken = new this.componentWindow.google.maps.places.AutocompleteSessionToken();
      this.placesService = new this.componentWindow.google.maps.places.PlacesService(
        this.$refs.map
      );
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public loadGoogleMapsJs() {
    const w = window as any;
    if (!w.googleMapsReady) w.googleMapsReady = this.onMapsReady;
    const url = new URL("https://maps.googleapis.com/maps/api/js");
    url.searchParams.set("key", `${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}`);
    url.searchParams.set("libraries", "places");
    url.searchParams.set("callback", "googleMapsReady");
    return this.$store.dispatch("utils/script/load", {
      key: "googleMaps",
      src: url.toString()
    });
  }

  public onMapsReady() {
    /* Will be called when Google Maps is ready */
  }

  public async emitResult(place: any) {
    if (place) {
      this.$emit(
        "onResult",
        {
          address_1: `${this.getAddressValue(place, [
            "street_number"
          ])} ${this.getAddressValue(place, ["route"])}`,
          address_2: this.getAddressValue(place, ["sublocality"]),
          postcode: this.getAddressValue(place, ["postal_code"]),
          city: this.getAddressValue(place, ["postal_town", "locality"]),
          region_id: await this.getAddressRegion(
            this.getAddressValue(place, ["administrative_area_level_1"]),
            this.getAddressValue(place, ["administrative_area_level_2"]),
            this.findCountryId(place) as string
          ),
          country_id: this.findCountryId(place)
        },
        this.address
      );
    }
  }

  public async getAddressRegion(
    regionLevel1: string,
    regionLevel2: string,
    countryId: ICountry["id"]
  ) {
    this.countryCode = this.$_.get(
      this.$_.find(this.countries, country => country.id === countryId),
      "code",
      this.$_.clone(countryId).toString()
    );

    await this.$store.dispatch("constants/getRegions", {
      countryId: countryId,
      countryCode: this.countryCode
    });

    return Promise.resolve(
      this.$_.get(
        this.$_.find(this.getRegions, region => {
          return this.$_.includes(
            [regionLevel1.toLowerCase(), regionLevel2.toLowerCase()],
            region.name.toLowerCase()
          );
        }),
        "id",
        null
      )
    );
  }

  public getAddressValue(addressComponents: any[], fields: string[]) {
    return this.$_.get(
      this.$_.find(addressComponents, addressComponent => {
        return !!this.$_.find(addressComponent.types, type => {
          return !!this.$_.find(fields, field => {
            return field === type;
          });
        });
      }),
      "long_name",
      ""
    );
  }

  public findCountryId(addressComponents: any) {
    return this.$_.get(
      this.$_.find(this.countries, country => {
        return (
          country.code ===
          this.$_.get(
            this.$_.find(addressComponents, addressComponent => {
              return !!this.$_.find(
                addressComponent.types,
                type => type === "country"
              );
            }),
            "short_name",
            ""
          )
        );
      }),
      "id",
      ""
    );
  }

  public displaySuggestions(predictions, status) {
    if (
      status === this.componentWindow.google.maps.places.PlacesServiceStatus.OK
    ) {
      this.predictions = predictions;
    } else if (
      status ===
      this.componentWindow.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
    ) {
      this.predictions = [];
    } else {
      this.showError(status);
    }
    this.isSearching = false;
  }

  public displayResult(result, status) {
    if (
      status === this.componentWindow.google.maps.places.PlacesServiceStatus.OK
    ) {
      this.emitResult(this.$_.get(result, "address_components", null));
    } else if (
      status ===
      this.componentWindow.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
    ) {
      this.predictions = [];
    } else {
      this.showError(status);
    }

    this.sessionToken = new this.componentWindow.google.maps.places.AutocompleteSessionToken();
    this.isSearching = false;
  }

  public onAddressSelected(option) {
    if (option) {
      this.addressSelected = true;
      this.placesService.getDetails(
        {
          placeId: option.place_id,
          sessionToken: this.sessionToken,
          fields: ["address_components"]
        },
        this.displayResult
      );
    } else {
      this.addressSelected = false;
    }
  }

  public findPredictions() {
    if (!this.addressSelected) {
      if (this.address.length === 0) {
        this.predictions = [];
      } else {
        this.isSearching = true;
        this.autocompleteService.getPlacePredictions(
          {
            input: this.address,
            sessionToken: this.sessionToken,
            fields: ["address_components"]
          },
          this.displaySuggestions
        );
      }
    }
  }

  public showError(status) {
    this.predictions = [];
    this.$buefy.toast.open({
      message: this.getErrorMessage(status) as string,
      type: "is-danger"
    });
  }

  public getErrorMessage(status) {
    switch (status) {
      case this.componentWindow.google.maps.places.PlacesServiceStatus
        .INVALID_REQUEST:
        return this.$t("_sentence.google_invalid_request");
      case this.componentWindow.google.maps.places.PlacesServiceStatus
        .NOT_FOUND:
        return this.$t("_sentence.google_not_found");
      case this.componentWindow.google.maps.places.PlacesServiceStatus
        .OVER_QUERY_LIMIT:
        return this.$t("_sentence.google_over_query_limit");
      case this.componentWindow.google.maps.places.PlacesServiceStatus
        .REQUEST_DENIED:
        return this.$t("_sentence.google_request_denied");
      default:
        return this.$t("_sentence.google_unknown_error");
    }
  }
}
